/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { processExcerpt } from '../../../../../../../common/utils/search-result-utils';

import { convertVideoDuration } from './convert-video-duration';
import { PastMeetings } from './past-meetings';
import { BaseVideoSearchResult } from './shared/base-video-search-result';
import { type VideoResultRendererProps } from './shared/types';

// Appends the date to a title as "{title} - {formatted date}" using the user's locale.
export const formatTitleWithDate = (
	title: string,
	lastModifiedDate: string | null | undefined,
	formatDate: ReturnType<typeof useIntl>['formatDate'],
): string => {
	if (!lastModifiedDate) {
		return title;
	}

	const formattedDate = formatDate(new Date(lastModifiedDate), {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});

	return `${title} - ${formattedDate}`;
};

// Renderer for MS Teams meeting transcript search results (no thumbnail/duration/chapters).
export const MeetingTranscriptRenderer: (props: VideoResultRendererProps) => JSX.Element = (
	props: VideoResultRendererProps,
) => {
	const {
		data,
		formatMessage,
		linkedResults,
		thirdPartyConfigs,
		onResultClicked,
		onResultContextMenu,
		position,
	} = props;
	const { excerpt, title, lastModifiedDate, entity } = data;
	const { durationInSeconds } = entity || {};
	const { formatDate } = useIntl();

	const videoDuration = convertVideoDuration(durationInSeconds, { readable: true, formatMessage });

	const body = <DescriptionBody description={processExcerpt(excerpt, true)} />;
	const customTitle = formatTitleWithDate(title, lastModifiedDate, formatDate);

	// Append each occurrence's date to its title (so identical series names are distinguishable) and
	// attach a video-length label.
	const pastMeetingLinkedResults = linkedResults?.map((linkedResult) => ({
		...linkedResult,
		title: linkedResult.title
			? formatTitleWithDate(linkedResult.title, linkedResult.lastModifiedDate, formatDate)
			: linkedResult.title,
		videoDurationLabel: convertVideoDuration(linkedResult.durationInSeconds, {
			readable: true,
			formatMessage,
		}),
	}));

	const pastMeetings =
		pastMeetingLinkedResults && pastMeetingLinkedResults.length > 0 ? (
			<PastMeetings
				data={data}
				linkedResults={pastMeetingLinkedResults}
				thirdPartyConfigs={thirdPartyConfigs}
				onResultClicked={onResultClicked}
				onResultContextMenu={onResultContextMenu}
				position={position}
			/>
		) : undefined;

	return (
		<BaseVideoSearchResult
			{...props}
			body={body}
			customTitle={customTitle}
			additionalMetadataParts={[['video-duration', videoDuration]]}
			footer={pastMeetings}
			hasRelatedLinks={!!pastMeetings}
		/>
	);
};
